import '../styles.css';
import React, { useMemo, useState, useRef } from 'react';
import {
  View,
  Tooltip,
} from '@smarterservices/smarter-ui';
import PointAnnotation from './PointAnnotation';
import RangeAnnotation from './RangeAnnotation';
import { useExamSessionStore, getStore } from '../../../../stores/StoreContext';
import CONSTANTS from '../../../../config/constants';

const hrStyle = {
  position: 'relative',
  top: '3px',
  border: 'none',
  height: '1px',
  backgroundColor: '#CCC',
  marginTop: '5px',
  zIndex: 10,
};

/**
 * Calculate row assignments for overlapping range annotations
 * Uses a greedy algorithm to pack annotations into rows, then reorders
 * so the most populated row is in the middle
 */
const calculateAnnotationRows = (rangeAnnotations) => {
  if (!rangeAnnotations || rangeAnnotations.length === 0) return { rowAssignments: {}, totalRows: 0 };

  const POINT_ANNOTATION_DURATION = 5; // Treat point annotations as 5 seconds duration for layout purposes

  // Sort by start time, then by duration (longer first)
  const sorted = [...rangeAnnotations].sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    const durationA = a.duration || POINT_ANNOTATION_DURATION;
    const durationB = b.duration || POINT_ANNOTATION_DURATION;
    return durationB - durationA; // Longer durations first
  });

  const rows = []; // Each row tracks the end time of the last annotation in that row
  const rowAssignments = {}; // Map annotation sid to row index

  for (const annotation of sorted) {
    const start = annotation.start;
    // For point annotations (no duration), use a small duration so they don't block the entire timeline
    const end = annotation.duration
      ? start + annotation.duration
      : start + POINT_ANNOTATION_DURATION;

    // Find the first row where this annotation fits (no overlap)
    let assignedRow = -1;
    for (let i = 0; i < rows.length; i++) {
      if (start >= rows[i]) {
        assignedRow = i;
        rows[i] = end; // Update row's end time
        break;
      }
    }

    // If no row found, create a new one
    if (assignedRow === -1) {
      assignedRow = rows.length;
      rows.push(end);
    }

    rowAssignments[annotation.sid] = assignedRow;
  }

  // Count annotations per row
  const rowCounts = new Array(rows.length).fill(0);
  Object.values(rowAssignments).forEach(row => {
    rowCounts[row]++;
  });

  // Create array of [originalRowIndex, count] and sort by count descending
  const rowsByCount = rowCounts.map((count, index) => ({ originalIndex: index, count }))
    .sort((a, b) => b.count - a.count);

  // Create new row mapping: most populated in middle, alternating outward
  const totalRows = rows.length;
  const middleRow = Math.floor(totalRows / 2);
  const oldToNewRowMap = {};

  // Place rows alternating from center outward
  for (let i = 0; i < rowsByCount.length; i++) {
    const { originalIndex } = rowsByCount[i];

    if (i === 0) {
      // Most populated row goes in the middle
      oldToNewRowMap[originalIndex] = middleRow;
    } else {
      // Alternate above and below center
      const offset = Math.ceil(i / 2);
      const isAbove = i % 2 === 1;
      oldToNewRowMap[originalIndex] = isAbove ? middleRow - offset : middleRow + offset;
    }
  }

  // Remap all row assignments
  const remappedAssignments = {};
  Object.entries(rowAssignments).forEach(([sid, oldRow]) => {
    remappedAssignments[sid] = oldToNewRowMap[oldRow];
  });

  return { rowAssignments: remappedAssignments, totalRows: rows.length };
};

const Annotations = ({ type }) => {
  const annotations = useExamSessionStore((state) => state.annotations);
  const PlayerStore = getStore('playerStore');
  const [tooltipContent, setTooltipContent] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipTarget, setTooltipTarget] = useState(null);
  const annotationCategoryRef = useRef(null);

  const managingAnnotation = useExamSessionStore(
    (state) => state.managingAnnotation
  );

  // Tooltip handlers - defined before useMemo
  const handleAnnotationMouseEnter = (e, annotation) => {
    setTooltipContent(annotation.type?.name);
    setTooltipTarget(e.currentTarget);
    setShowTooltip(true);
  };

  const handleAnnotationMouseLeave = () => {
    setShowTooltip(false);
    setTooltipTarget(null);
  };

  // Use useMemo to create annotation components only when necessary
  const annotationsToDisplay = useMemo(() => {
    const typeAnnotations = annotations[type?.id];
    const annotationsForDisplay = [];

    if (typeAnnotations && typeAnnotations.length) {
      // Calculate row assignments for ALL annotations (both range and point)
      // This ensures they don't overlap each other in the timeline
      const { rowAssignments, totalRows } = calculateAnnotationRows(typeAnnotations);

      // Render all annotations with row assignments
      for (const item of typeAnnotations) {
        const stableKey = `annotation_${item.type.type}_${item.sid}`;
        const row = rowAssignments[item.sid] || 0;

        // remove color b/c it will be auto generated based on severity
        delete item.color;

        annotationsForDisplay.push(
          <RangeAnnotation
            key={stableKey}
            annotation={item}
            type={type}
            row={row}
            totalRows={totalRows}
            onMouseEnter={handleAnnotationMouseEnter}
            onMouseLeave={handleAnnotationMouseLeave}
          />
        );
      }
      const height = CONSTANTS.PLAYER.MARKER_DEFAULT_ROW_HEIGHT + ((totalRows - 1) * CONSTANTS.PLAYER.MARKER_ROW_HEIGHT);
      PlayerStore.setMarkersDisplayProperty(type.id, 'height', height);
    }

    return annotationsForDisplay;
  }, [annotations, type?.id, handleAnnotationMouseEnter, handleAnnotationMouseLeave]);

  return (
    <>
      <View
        as="div"
        margin="x-small none none none"
        withVisualDebug={false}
        elementRef={(el) => {
          annotationCategoryRef.current = el;
        }}
      >
        {annotationsToDisplay}
        <hr style={hrStyle} />


      </View>
      {tooltipTarget && (
        <Tooltip
          renderTip={tooltipContent}
          isShowingContent={showTooltip}
          placement="top"
          constrain="none"
          positionTarget={tooltipTarget}
          themeOverride={{
            fontSize: '0.7rem',
            padding: '0.5rem'
          }}
          styles={{
            zIndex: 99999,
            width: '300px',
          }}
        />
      )}
    </>
  );
};

export default React.memo(Annotations);
